import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Loader2, Calculator, TrendingUp, ChevronRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { trackEvent as trackAction } from "../../lib/analytics";
import { useAuth } from "../../context/AuthContext";

/** Mortgage funnel: CTA → calc → qualification → thank-you. Fires analytics
 *  for every stage so the admin funnel-dashboard can compute conversion. */
const fmtEur = (n) => {
  const v = Math.round(Number(n) || 0);
  return "€ " + v.toLocaleString("nl-NL");
};
const parseEur = (s) => Number(String(s).replace(/[^\d]/g, "")) || 0;

const ENERGY_LABELS = ["A++++", "A+++", "A++", "A+", "A", "B", "C", "D", "E", "F", "G"];

export const MortgageWidget = ({ listing }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState("calc"); // calc | form | done
  const [cfg, setCfg] = useState(null);
  const [calc, setCalc] = useState(null);
  const [price, setPrice] = useState(0);
  const [ownFunds, setOwnFunds] = useState(0);
  const [energyLabel, setEnergyLabel] = useState("");
  const [form, setForm] = useState({
    buying_alone_or_together: "alone",
    applicant_income: "",
    partner_income: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    consent: false,
  });
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [doneLeadId, setDoneLeadId] = useState(null);

  const askingPrice = Number(listing?.price || 0);
  const propId = listing?.id;

  // Fire a one-time CTA-view event when the component mounts (visible section).
  useEffect(() => {
    if (!listing) return;
    trackAction("mortgage_cta_view", { property_id: propId, property_price: askingPrice, property_city: listing.city });
    api("/mortgage/config").then(setCfg).catch(() => {});
    // Pre-fill editable fields from listing
    setPrice(askingPrice);
    setEnergyLabel(listing.energy_label && listing.energy_label !== "—" ? listing.energy_label : "");
    if (user) {
      const [first = "", ...rest] = (user.name || "").split(" ");
      setForm((p) => ({
        ...p,
        first_name: first,
        last_name: rest.join(" ") || "",
        email: user.email || "",
        phone: user.phone || "",
      }));
    }
  }, [propId]);

  const openCTA = () => {
    trackAction("mortgage_cta_click", { property_id: propId, property_price: askingPrice });
    setOpen(true);
    setStage("calc");
    setStep(1);
  };

  const runCalc = async () => {
    try {
      trackAction("mortgage_calculation_started", { property_id: propId });
      const r = await api("/mortgage/calc", { method: "POST", body: JSON.stringify({ price: price || askingPrice, own_funds: ownFunds }) });
      setCalc({ ...r, energy_label: energyLabel || null });
      trackAction("mortgage_calculation_completed", { property_id: propId, monthly: r.monthly_payment, desired: r.desired_mortgage, energy_label: energyLabel || null });
    } catch (e) {
      toast.error(e.message);
    }
  };

  const startForm = () => {
    trackAction("mortgage_lead_form_started", { property_id: propId });
    setStage("form");
    setStep(1);
  };

  const next = () => {
    trackAction("mortgage_lead_form_step_completed", { property_id: propId, step });
    setStep((s) => s + 1);
  };

  const submit = async () => {
    if (!form.consent) { toast.error("Toestemming is verplicht."); return; }
    if (!form.email || !form.first_name) { toast.error("Vul je naam en e-mail in."); return; }
    setSubmitting(true);
    try {
      const payload = {
        lead_type: "mortgage_advice",
        name: `${form.first_name} ${form.last_name}`.trim(),
        email: form.email.trim(),
        phone: form.phone?.trim() || null,
        city: listing.city || null,
        contact_preference: "phone",
        consent_partners: !!form.consent,
        source_screen: typeof window !== "undefined" ? window.location.pathname : null,
        utm_source: new URLSearchParams(window.location.search).get("utm_source") || null,
        utm_medium: new URLSearchParams(window.location.search).get("utm_medium") || null,
        utm_campaign: new URLSearchParams(window.location.search).get("utm_campaign") || null,
        type_data: {
          property_id: propId,
          property_address: listing.street ? `${listing.street}${listing.house_number ? " " + listing.house_number : ""}` : null,
          property_city: listing.city,
          property_url: window.location.href,
          listing_source: listing.source || "woningalert",
          asking_price: askingPrice,
          user_entered_price: effectivePrice,
          own_funds: ownFunds,
          energy_label: energyLabel || listing.energy_label || null,
          desired_mortgage_amount: calc?.desired_mortgage,
          applicant_income: parseEur(form.applicant_income) || null,
          partner_income: form.buying_alone_or_together === "together" ? (parseEur(form.partner_income) || null) : null,
          buying_alone_or_together: form.buying_alone_or_together,
          estimated_monthly_payment: calc?.monthly_payment,
          calculation_interest_rate: calc?.rate_pct,
          calculation_term: calc?.term_years,
          cta_origin: "koop_detail_mortgage_widget",
          consent_text: "Ik ga ermee akkoord dat WoningAlert mijn gegevens deelt met een aangesloten hypotheekadviseur om contact met mij op te nemen over mijn hypotheekmogelijkheden.",
          consent_version: "v1",
        },
      };
      const res = await api("/vastgoed-leads", { method: "POST", body: JSON.stringify(payload) });
      setDoneLeadId(res.id);
      trackAction("mortgage_lead_submitted", { property_id: propId, lead_id: res.id, duplicate: !!res.duplicate });
      setStage("done");
    } catch (e) {
      toast.error(e.message);
    } finally { setSubmitting(false); }
  };

  if (!listing || askingPrice <= 0) return null;
  const effectivePrice = price || askingPrice;
  const desired = Math.max(0, effectivePrice - ownFunds);
  // NL NHG indicatieve extra leenruimte op basis van energielabel (2026 schatting).
  const energyBonusMap = { "A++++": 50000, "A+++": 40000, "A++": 30000, "A+": 20000, "A": 10000, "B": 5000, "C": 5000 };
  const energyBonus = energyBonusMap[energyLabel] || 0;

  return (
    <>
      {/* Prominent CTA under the price */}
      <div className="mt-3 rounded-2xl border border-brandblue/20 bg-brandblue-light/30 p-4" data-testid="mortgage-cta-section">
        <div className="flex items-start gap-3">
          <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brandblue text-white sm:flex">
            <Calculator size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-navy">Wat worden jouw maandlasten?</p>
            <p className="mt-0.5 text-xs text-slate-500">Ontdek wat deze woning ongeveer per maand kost en wat je kunt lenen.</p>
            <Button
              onClick={openCTA}
              data-testid="mortgage-cta-btn"
              className="mt-3 w-full rounded-xl bg-brandblue text-white hover:bg-brandblue/90 sm:w-auto"
            >
              <Calculator size={16} className="mr-2" /> Wat worden mijn maandlasten?
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md p-0 sm:max-w-lg" data-testid="mortgage-dialog">
          {stage === "calc" && (
            <>
              <DialogHeader className="border-b border-border p-5">
                <DialogTitle className="text-lg">Wat worden jouw maandlasten?</DialogTitle>
                <p className="mt-1 text-xs text-slate-500">
                  {listing.street ? `${listing.street}${listing.house_number ? " " + listing.house_number : ""}, ` : ""}
                  {listing.city}
                </p>
              </DialogHeader>
              <div className="space-y-4 p-5">
                <div>
                  <Label htmlFor="mortgage-price" className="text-sm font-semibold text-navy">Koopprijs</Label>
                  <Input
                    id="mortgage-price"
                    data-testid="mortgage-price"
                    inputMode="numeric"
                    value={price ? fmtEur(price) : ""}
                    placeholder={fmtEur(askingPrice)}
                    onChange={(e) => setPrice(parseEur(e.target.value))}
                    className="mt-1"
                  />
                  {price && price !== askingPrice && (
                    <p className="mt-1 text-[11px] text-slate-400">Vraagprijs: {fmtEur(askingPrice)}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="own-funds" className="text-sm font-semibold text-navy">Eigen geld</Label>
                  <Input
                    id="own-funds"
                    data-testid="mortgage-own-funds"
                    inputMode="numeric"
                    value={ownFunds ? fmtEur(ownFunds) : ""}
                    placeholder="€ 0"
                    onChange={(e) => setOwnFunds(parseEur(e.target.value))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="energy-label" className="text-sm font-semibold text-navy">Energielabel</Label>
                  <select
                    id="energy-label"
                    data-testid="mortgage-energy-label"
                    value={energyLabel}
                    onChange={(e) => setEnergyLabel(e.target.value)}
                    className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                  >
                    <option value="">Onbekend / niet opgegeven</option>
                    {ENERGY_LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                  {energyBonus > 0 && (
                    <p className="mt-1 text-[11px] text-success">
                      Label {energyLabel}: tot {fmtEur(energyBonus)} extra lenen mogelijk (NHG, indicatief).
                    </p>
                  )}
                </div>
                <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
                  Gewenste hypotheek: <strong className="text-navy">{fmtEur(desired)}</strong>
                </div>

                {!calc ? (
                  <Button data-testid="mortgage-calc-btn" onClick={runCalc} className="w-full rounded-xl bg-navy text-white">
                    Bereken mijn indicatie
                  </Button>
                ) : (
                  <>
                    <div className="rounded-2xl bg-gradient-to-br from-brandblue-light to-white p-4 ring-1 ring-brandblue/20" data-testid="mortgage-result">
                      <div className="text-xs font-semibold uppercase tracking-wide text-brandblue">Geschatte bruto maandlasten</div>
                      <div className="mt-1 font-display text-3xl font-extrabold text-navy">{fmtEur(calc.monthly_payment)} <span className="text-base font-semibold text-slate-500">p/m</span></div>
                      <div className="mt-2 space-y-0.5 text-xs text-slate-500">
                        <div>Hypotheek: {fmtEur(calc.desired_mortgage)}</div>
                        <div>Rente: {calc.rate_pct}% · Looptijd: {calc.term_years} jaar · Annuïtair</div>
                      </div>
                      <p className="mt-3 text-[11px] leading-snug text-slate-500">
                        Dit is een indicatieve berekening. Je daadwerkelijke maandlasten en maximale hypotheek zijn afhankelijk van onder andere inkomen, rente, eigen geld en persoonlijke situatie.
                      </p>
                    </div>
                    <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4">
                      <p className="font-display text-sm font-bold text-navy">Wil je weten of je deze woning kunt betalen?</p>
                      <p className="mt-1 text-xs text-slate-600">Een hypotheekadviseur kan vrijblijvend voor je berekenen wat je maximaal kunt lenen en wat je maandlasten daadwerkelijk worden.</p>
                      <div className="mt-3 flex gap-2">
                        <Button data-testid="mortgage-start-form" onClick={startForm} className="flex-1 rounded-xl bg-orange-600 text-white hover:bg-orange-700">
                          <TrendingUp size={16} className="mr-2" /> Gratis hypotheekcheck aanvragen
                        </Button>
                        <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Niet nu</Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {stage === "form" && (
            <>
              <DialogHeader className="border-b border-border p-5">
                <DialogTitle className="text-lg">Gratis hypotheekcheck</DialogTitle>
                <p className="mt-1 text-xs text-slate-500">Stap {step} van 4</p>
              </DialogHeader>
              <div className="space-y-4 p-5">
                {step === 1 && (
                  <>
                    <Label className="text-sm font-semibold text-navy">Koop je alleen of samen?</Label>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {[{ v: "alone", l: "Alleen" }, { v: "together", l: "Samen" }].map((o) => (
                        <button
                          key={o.v}
                          data-testid={`mortgage-together-${o.v}`}
                          onClick={() => setForm({ ...form, buying_alone_or_together: o.v })}
                          className={`rounded-xl border p-3 text-sm font-semibold transition ${form.buying_alone_or_together === o.v ? "border-brandblue bg-brandblue-light text-navy" : "border-border text-slate-600 hover:border-brandblue"}`}
                        >{o.l}</button>
                      ))}
                    </div>
                    <Button data-testid="mortgage-next-1" onClick={next} className="mt-2 w-full rounded-xl bg-navy text-white">Volgende <ChevronRight size={16} className="ml-1" /></Button>
                  </>
                )}
                {step === 2 && (
                  <>
                    <Label className="text-sm font-semibold text-navy">Bruto jaarinkomen</Label>
                    <Input
                      data-testid="mortgage-income-applicant"
                      inputMode="numeric" placeholder="€ 50.000"
                      value={form.applicant_income ? fmtEur(parseEur(form.applicant_income)) : ""}
                      onChange={(e) => setForm({ ...form, applicant_income: e.target.value })}
                    />
                    {form.buying_alone_or_together === "together" && (
                      <>
                        <Label className="mt-2 text-sm font-semibold text-navy">Bruto jaarinkomen partner</Label>
                        <Input
                          data-testid="mortgage-income-partner"
                          inputMode="numeric" placeholder="€ 40.000"
                          value={form.partner_income ? fmtEur(parseEur(form.partner_income)) : ""}
                          onChange={(e) => setForm({ ...form, partner_income: e.target.value })}
                        />
                      </>
                    )}
                    <p className="text-xs text-slate-400">Weet je het niet precies? Laat leeg en de adviseur neemt het mee.</p>
                    <Button data-testid="mortgage-next-2" onClick={next} className="w-full rounded-xl bg-navy text-white">Volgende <ChevronRight size={16} className="ml-1" /></Button>
                  </>
                )}
                {step === 3 && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-sm font-semibold text-navy">Voornaam</Label>
                        <Input data-testid="mortgage-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-navy">Achternaam</Label>
                        <Input data-testid="mortgage-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-navy">E-mailadres</Label>
                      <Input data-testid="mortgage-email" type="email" inputMode="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-navy">Telefoonnummer</Label>
                      <Input data-testid="mortgage-phone" type="tel" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </div>
                    <Button data-testid="mortgage-next-3" onClick={next} className="w-full rounded-xl bg-navy text-white">Volgende <ChevronRight size={16} className="ml-1" /></Button>
                  </>
                )}
                {step === 4 && (
                  <>
                    <div className="rounded-2xl bg-slate-50 p-3 text-xs text-slate-600 leading-relaxed">
                      Je gegevens worden alléén gedeeld na jouw expliciete toestemming hieronder. WoningAlert zelf geeft geen hypotheekadvies — advies wordt gegeven door een aangesloten hypotheekadviseur.
                    </div>
                    <label className="flex items-start gap-3 rounded-xl border border-border p-3">
                      <Checkbox
                        checked={form.consent}
                        onCheckedChange={(c) => setForm({ ...form, consent: !!c })}
                        data-testid="mortgage-consent"
                      />
                      <span className="text-xs text-slate-600">
                        Ik ga ermee akkoord dat WoningAlert mijn gegevens deelt met een aangesloten hypotheekadviseur om contact met mij op te nemen over mijn hypotheekmogelijkheden. Zie onze <a href="/privacy" className="text-brandblue underline">privacyverklaring</a>.
                      </span>
                    </label>
                    <Button
                      data-testid="mortgage-submit"
                      disabled={submitting || !form.consent}
                      onClick={submit}
                      className="w-full rounded-xl bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
                    >
                      {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                      Vraag gratis hypotheekcheck aan
                    </Button>
                    <p className="text-center text-xs text-slate-400">Gratis en vrijblijvend.</p>
                  </>
                )}
              </div>
            </>
          )}

          {stage === "done" && (
            <div className="p-6 text-center" data-testid="mortgage-done">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success/10 text-success">
                <CheckCircle2 size={28} />
              </div>
              <h3 className="mt-3 font-display text-xl font-extrabold text-navy">Je hypotheekcheck is aangevraagd</h3>
              <p className="mt-2 text-sm text-slate-600">Bedankt! Een aangesloten hypotheekadviseur neemt contact met je op om te bekijken wat er in jouw situatie mogelijk is.</p>
              <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-slate-50">
                {listing.image && <img src={listing.image} alt="" className="h-28 w-full object-cover" />}
                <div className="p-3 text-left">
                  <div className="text-sm font-semibold text-navy">{listing.street || listing.title}{listing.house_number ? " " + listing.house_number : ""}</div>
                  <div className="text-xs text-slate-500">{listing.city} · {fmtEur(askingPrice)}</div>
                </div>
              </div>
              <Button data-testid="mortgage-back" onClick={() => setOpen(false)} className="mt-4 w-full rounded-xl bg-navy text-white">Terug naar de woning</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MortgageWidget;
